import React, { useEffect, useRef, useState } from 'react'
import { TabInterface, TabsComponentProps } from '../utils'
import { ShortcutKeys } from '../utils/tab-indexing-contants'
import Tooltip from './Tooltip'

export const Tabs: React.FC<TabsComponentProps> = ({
  tabs,
  onChange,
  defaultActive = tabs[0],
  width = 'max-w-[calc(100%-19rem)]',
  height = '',
  children,
  fontWeight = 'font-light',
  activeFontColor = 'text-secondary-500',
  bottomColor = 'border-secondary-500',
  inActiveFontColor = 'text-accent-400',
  tabsWrapperClassName = '',
  tabStyling = '',
  tabsContentClassName = '',
  tabIndex,
  scrollIntoViewOnLoad = true,
  activeBackgroundColor = '',
  inActiveBackgroundColor = '',
  roundTabs = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabInterface>(defaultActive)
  const tabsRef = useRef<HTMLDivElement>(null)
  const previousActiveKeyRef = useRef<string | number | undefined>(
    defaultActive?.key ?? defaultActive?.label
  )

  useEffect(() => {
    const defaultTab = tabs?.find((item) => {
      if (item?.label === defaultActive?.label) {
        if (item?.key && defaultActive?.key && item?.key !== defaultActive?.key)
          return false
        return true
      }
      if (item?.key && defaultActive?.key && item?.key === defaultActive?.key)
        return true
    })
    const nextActive = defaultTab ?? defaultActive
    const nextKey = nextActive?.key ?? nextActive?.label
    const activeKeyChanged =
      String(nextKey ?? '') !== String(previousActiveKeyRef.current ?? '')

    // Always sync to the tab object from the current `tabs` array (new component refs).
    // Only scroll when the active key actually changes — identity-only tab updates
    // (e.g. Redux workflowDefinition refresh) were causing flicker via scrollIntoView.
    previousActiveKeyRef.current = nextKey
    setActiveTab(nextActive)

    if (!activeKeyChanged || !tabsRef.current || !scrollIntoViewOnLoad) return

    const activeTabIndex = tabs.findIndex((tab) =>
      tab.key != null && nextActive?.key != null
        ? tab.key === nextActive.key
        : tab.label === nextActive?.label
    )
    if (activeTabIndex === -1) return

    const activeTabElement = tabsRef.current.children[
      activeTabIndex
    ] as HTMLDivElement
    activeTabElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [defaultActive, tabs, scrollIntoViewOnLoad])

  const handleClick = (tab: TabInterface, index: number) => {
    if (tab?.disabledKey) return
    setActiveTab(tab)
    onChange && onChange(tab?.label, index)
  }

  const tabsContainerClass = `flex overflow-x-auto items-center ${width} hide-scrollbar ${
    roundTabs ? 'rounded' : ''
  } h-[${height}]`

  return (
    <div className={tabsWrapperClassName}>
      <div className="flex justify-between items-center">
        <div className={tabsContainerClass} ref={tabsRef}>
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={`whitespace-nowrap font-primary block p-3 text-xs ${tabStyling} ${
                (
                  activeTab?.key
                    ? activeTab?.key === tab?.key
                    : activeTab?.label === tab?.label
                )
                  ? `border-b-2 ${bottomColor} font-light ${activeBackgroundColor} ${activeFontColor}`
                  : `${inActiveBackgroundColor} ${inActiveFontColor}`
              }
                ${
                  tab?.disabledKey
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
              onClick={() => handleClick(tab, index)}
              tabIndex={tabIndex ?? 0}
              onKeyDown={(e) => {
                if (
                  e?.key === ShortcutKeys.Enter ||
                  e?.key === ShortcutKeys.Space
                ) {
                  handleClick(tab, index)
                }
              }}
            >
              {tab?.tooltipConfig ? (
                <Tooltip
                  content={tab?.tooltipConfig?.tooltipContent ?? ''}
                  position={tab?.tooltipConfig?.position || 'top'}
                >
                  <span className={`cursor-pointer ${fontWeight} capitalize`}>
                    <div
                      className="ck-content break-words"
                      dangerouslySetInnerHTML={{ __html: tab?.label ?? '' }}
                    ></div>
                  </span>
                </Tooltip>
              ) : (
                <span className={`cursor-pointer ${fontWeight} capitalize`}>
                  {tab?.label}
                </span>
              )}
            </div>
          ))}
        </div>
        <div>{children}</div>
      </div>
      <div className={tabsContentClassName} key={activeTab?.key}>
        {activeTab?.component}
      </div>
    </div>
  )
}

export default Tabs
